import { Component, Inject, PLATFORM_ID, AfterViewInit, ViewChild, TemplateRef, OnInit, HostListener, OnDestroy } from '@angular/core';
import { isPlatformBrowser, PlatformLocation, ViewportScroller } from '@angular/common';
import { Router, NavigationStart } from '@angular/router';
import { Subscription } from 'rxjs';
import { TourService } from './components/tour/tour.service';
import { DesktopDeviceCommandRunnerService } from './services/desktop-device-command-runner.service';
import { PosthogService } from './services/posthog.service';
import { ThemeService } from './services/theme.service';
import { ApplicationFontService } from './services/application-font.service';
import { AuthService } from './services/auth.service';
import { TimeTrackingService } from './services/time-tracking.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  host: {
    '[class.theme-light]': 'theme.isLight',
    '[attr.data-application-font]': 'applicationFont.font'
  }
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('tourTemplate') tourTemplate!: TemplateRef<any>;
  isServerAgencyRoute = false;
  private timerRecoverySubscription?: Subscription;
  private recoveredTimerContextKey: string | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private tourService: TourService,
    private posthogService: PosthogService,
    private router: Router,
    private viewportScroller: ViewportScroller,
    private desktopDeviceCommandRunnerService: DesktopDeviceCommandRunnerService,
    private platformLocation: PlatformLocation,
    private authService: AuthService,
    private timeTrackingService: TimeTrackingService,
    public theme: ThemeService,
    public applicationFont: ApplicationFontService
  ) {
    if (!isPlatformBrowser(this.platformId)) {
      this.isServerAgencyRoute = this.platformLocation.pathname.startsWith('/myagency');
    }
  }

  ngOnInit(): void {
    this.desktopDeviceCommandRunnerService.ensureStarted();

    // Scroll to top instantly on navigation start (before new content renders)
    if (isPlatformBrowser(this.platformId)) {
      this.timerRecoverySubscription = this.authService.afAuth.authState.subscribe(user => {
        void this.recoverActiveTimerForUser(user);
      });

      this.router.events.subscribe(event => {
        if (event instanceof NavigationStart) {
          this.viewportScroller.scrollToPosition([0, 0]);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.timerRecoverySubscription?.unsubscribe();
  }

  ngAfterViewInit(): void {
    // Register the tour template with the service
    if (this.tourTemplate) {
      this.tourService.registerTourTemplate(this.tourTemplate);
    }

    // Only add global helper in browser
    if (isPlatformBrowser(this.platformId)) {
      (window as any).startTour = (tourId: string) => {
        this.tourService.startTour(tourId);
      };
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleGlobalThemeShortcut(event: KeyboardEvent): void {
    if (
      !isPlatformBrowser(this.platformId)
      || event.defaultPrevented
      || event.repeat
      || !this.isThemeToggleShortcut(event)
      || this.isTextInputFocused()
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.theme.toggle();
  }

  private isThemeToggleShortcut(event: KeyboardEvent): boolean {
    return event.code === 'Backquote'
      && event.altKey
      && event.shiftKey
      && !event.ctrlKey
      && !event.metaKey;
  }

  private isTextInputFocused(): boolean {
    const activeElement = document.activeElement;

    return !!activeElement && (
      activeElement.tagName === 'INPUT'
      || activeElement.tagName === 'TEXTAREA'
      || activeElement.tagName === 'SELECT'
      || activeElement.hasAttribute('contenteditable')
      || activeElement.getAttribute('role') === 'textbox'
    );
  }

  private async recoverActiveTimerForUser(user: { uid?: string } | null): Promise<void> {
    const userId = user?.uid;
    if (!userId) {
      this.recoveredTimerContextKey = null;
      return;
    }

    const existingAgencyId = this.authService.aid || this.authService.selectedAgency?.uid;
    if (existingAgencyId && this.recoveredTimerContextKey === `${userId}:${existingAgencyId}`) {
      return;
    }

    try {
      await this.authService.loadAuthData(userId);
      const agencyId = this.authService.aid || this.authService.selectedAgency?.uid || userId;
      const contextKey = `${userId}:${agencyId}`;

      if (this.recoveredTimerContextKey === contextKey) {
        return;
      }

      this.recoveredTimerContextKey = contextKey;
      await this.timeTrackingService.recoverActiveSession(userId, agencyId);
    } catch {
      this.recoveredTimerContextKey = null;
    }
  }
}
